import React, { useState, useEffect } from 'react';
import { imposedLeavesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Users, Globe, MapPin } from 'lucide-react';
import './Absences.css';

const TYPE_LABELS = {
  CONGE_PAYE:       'Congé payé',
  CONGE_SANS_SOLDE: 'Congé sans solde',
  RTT:              'RTT',
};

const SITES = ['FRANCE', 'BELGIQUE', 'ALGERIE', 'TUNISIE'];
const SITE_LABELS = { FRANCE: '🇫🇷 France', BELGIQUE: '🇧🇪 Belgique', ALGERIE: '🇩🇿 Algérie', TUNISIE: '🇹🇳 Tunisie' };

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const EMPTY_FORM = {
  titre:           '',
  date_debut:      '',
  date_fin:        '',
  type_conge:      'CONGE_PAYE',
  description:     '',
  cible_mode:      'tous',   // 'tous' | 'site' | 'users'
  site_filter:     [],
  cible_tous:      true,
  target_user_ids: [],
};

export default function AbsencesImposees() {
  const { user } = useAuth();
  const isAdmin = ['SUPER_ADMIN', 'ADMIN', 'RH'].includes(user?.role?.toUpperCase());

  const [items,   setItems]   = useState([]);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);  // null | {mode:'create'} | {mode:'edit', item}
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    imposedLeavesAPI.list()
      .then(r => setItems(r.data || []))
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (isAdmin) {
      imposedLeavesAPI.listSalaries()
        .then(r => setUsers(r.data || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: 'create' });
  };

  const openEdit = (item) => {
    setForm({
      titre:           item.titre,
      date_debut:      item.date_debut,
      date_fin:        item.date_fin,
      type_conge:      item.type_conge,
      description:     item.description || '',
      cible_mode:      item.cible_tous ? 'tous' : 'users',
      site_filter:     [],
      cible_tous:      item.cible_tous,
      target_user_ids: item.target_user_ids || [],
    });
    setModal({ mode: 'edit', item });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce congé imposé ?')) return;
    try {
      await imposedLeavesAPI.remove(id);
      toast.success('Supprimé');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleSave = async () => {
    if (!form.titre.trim())  { toast.error('Le titre est obligatoire'); return; }
    if (!form.date_debut)    { toast.error('Sélectionnez une date de début'); return; }
    if (!form.date_fin)      { toast.error('Sélectionnez une date de fin'); return; }
    if (form.date_fin < form.date_debut) { toast.error('Date de fin incorrecte'); return; }

    // Résoudre les IDs selon le mode de ciblage
    let resolvedIds = [];
    let cible_tous = false;
    if (form.cible_mode === 'tous') {
      cible_tous = true;
    } else if (form.cible_mode === 'site') {
      if (form.site_filter.length === 0) { toast.error('Sélectionnez au moins un site'); return; }
      resolvedIds = users.filter(u => form.site_filter.includes(u.site)).map(u => u.id);
      if (resolvedIds.length === 0) { toast.error('Aucun utilisateur trouvé pour ces sites'); return; }
    } else {
      if (form.target_user_ids.length === 0) { toast.error('Sélectionnez au moins un utilisateur'); return; }
      resolvedIds = form.target_user_ids;
    }

    setSaving(true);
    try {
      const payload = {
        titre:           form.titre,
        date_debut:      form.date_debut,
        date_fin:        form.date_fin,
        type_conge:      form.type_conge,
        description:     form.description || null,
        cible_tous,
        target_user_ids: resolvedIds,
      };
      if (modal.mode === 'create') {
        await imposedLeavesAPI.create(payload);
        toast.success('Congé imposé créé');
      } else {
        await imposedLeavesAPI.update(modal.item.id, payload);
        toast.success('Congé imposé mis à jour');
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (uid) => {
    setForm(f => ({
      ...f,
      target_user_ids: f.target_user_ids.includes(uid)
        ? f.target_user_ids.filter(x => x !== uid)
        : [...f.target_user_ids, uid],
    }));
  };

  const upcoming = items.filter(i => new Date(i.date_fin) >= new Date())
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
  const past = items.filter(i => new Date(i.date_fin) < new Date())
    .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ margin: 0 }}>Absences imposées</h1>
          <p style={{ margin: 0 }}>Congés fixés par l'employeur (ponts, fermetures, etc.)</p>
        </div>
        {isAdmin && (
          <div className="page-header-actions">
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={15}/> Ajouter un congé imposé
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"/></div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600 }}>
                À venir
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {upcoming.map(i => <ImposedCard key={i.id} item={i} isAdmin={isAdmin} onEdit={openEdit} onDelete={handleDelete}/>)}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div className="card">
              <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                Passés
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {past.map(i => <ImposedCard key={i.id} item={i} isAdmin={isAdmin} onEdit={openEdit} onDelete={handleDelete} past/>)}
              </div>
            </div>
          )}

          {items.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              Aucun congé imposé pour le moment.
            </div>
          )}
        </>
      )}

      {/* Modal création / édition */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>
                {modal.mode === 'create' ? 'Nouveau congé imposé' : 'Modifier le congé imposé'}
              </h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>
                <X size={16}/>
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Titre *</label>
              <input
                className="form-control"
                value={form.titre}
                onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                placeholder="Ex: Pont du 8 mai, Fermeture estivale…"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Date de début *</label>
                <input type="date" className="form-control" value={form.date_debut}
                  onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))}/>
              </div>
              <div className="form-group">
                <label className="form-label">Date de fin *</label>
                <input type="date" className="form-control" value={form.date_fin}
                  onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))}
                  min={form.date_debut}/>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Type de congé</label>
              <select className="form-control" value={form.type_conge}
                onChange={e => setForm(f => ({ ...f, type_conge: e.target.value }))}>
                <option value="CONGE_PAYE">Congé payé</option>
                <option value="RTT">RTT</option>
                <option value="CONGE_SANS_SOLDE">Congé sans solde</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Description <span style={{ color: 'var(--text-muted)' }}>(optionnel)</span></label>
              <textarea className="form-control" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Contexte, précisions…" style={{ resize: 'vertical' }}/>
            </div>

            {/* Cible */}
            <div className="form-group">
              <label className="form-label">Concerne</label>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                {[
                  { val: 'tous',  icon: <Globe size={13}/>,  label: 'Tous' },
                  { val: 'site',  icon: <MapPin size={13}/>, label: 'Par site' },
                  { val: 'users', icon: <Users size={13}/>,  label: 'Sélection manuelle' },
                ].map(opt => (
                  <label key={opt.val} className="abs-checkbox" style={{ cursor: 'pointer' }}>
                    <input type="radio" name="cible_mode" checked={form.cible_mode === opt.val}
                      onChange={() => setForm(f => ({ ...f, cible_mode: opt.val, site_filter: [], target_user_ids: [] }))}/>
                    {opt.icon} {opt.label}
                  </label>
                ))}
              </div>

              {/* Par site */}
              {form.cible_mode === 'site' && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  {SITES.map(s => (
                    <label key={s} className="abs-checkbox" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.site_filter.includes(s)}
                        onChange={() => setForm(f => ({
                          ...f,
                          site_filter: f.site_filter.includes(s)
                            ? f.site_filter.filter(x => x !== s)
                            : [...f.site_filter, s],
                        }))}/>
                      {SITE_LABELS[s]}
                    </label>
                  ))}
                </div>
              )}
              {form.cible_mode === 'site' && form.site_filter.length > 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                  {users.filter(u => form.site_filter.includes(u.site)).length} collaborateur(s) concerné(s)
                </div>
              )}

              {/* Sélection manuelle */}
              {form.cible_mode === 'users' && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12,
                              maxHeight: 200, overflowY: 'auto' }}>
                  {users.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Chargement…</div>
                  ) : users.map(u => (
                    <label key={u.id} className="abs-checkbox" style={{ display: 'flex', padding: '4px 0' }}>
                      <input type="checkbox" checked={form.target_user_ids.includes(u.id)}
                        onChange={() => toggleUser(u.id)}/>
                      <span>{u.prenom} {u.nom}
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.73rem' }}>
                          {' '}— {SITE_LABELS[u.site] || u.site}
                          {u.email ? ` · ${u.email}` : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {form.cible_mode === 'users' && form.target_user_ids.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  {form.target_user_ids.length} sélectionné(s)
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '…' : modal.mode === 'create' ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImposedCard({ item, isAdmin, onEdit, onDelete, past }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 16px', borderRadius: 10,
      border: '1px solid var(--border)',
      background: past ? 'var(--surface-alt, rgba(255,255,255,0.02))' : 'var(--surface)',
      opacity: past ? 0.7 : 1,
      flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.titre}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {fmt(item.date_debut)} → {fmt(item.date_fin)} · {item.nb_jours} j · {TYPE_LABELS[item.type_conge]}
        </div>
        {item.description && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{item.description}</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {item.cible_tous ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Globe size={12}/> Tous
          </span>
        ) : (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Users size={12}/> {item.target_user_ids?.length || 0} personne{(item.target_user_ids?.length || 0) > 1 ? 's' : ''}
          </span>
        )}
        {isAdmin && !past && (
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => onEdit(item)}><Pencil size={13}/></button>
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-600)' }} onClick={() => onDelete(item.id)}><Trash2 size={13}/></button>
          </>
        )}
      </div>
    </div>
  );
}
